// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import * as Rematrix from 'rematrix';
import { debounce, first } from 'lodash';
import { createStructuredSelector } from 'reselect';
import ResizeObserver from 'resize-observer-polyfill';

// Utils
import { isDebugEnabled } from '../../debug/debugUtil';
import lerp from '../../../common/maths/lerp';
import { hasChanged } from '../../utils/react/propsComparisons';
import { sendAmplitudeZoomEvent } from './utils/zoomAnalyticsUtils';
import * as matrixLib from '../../../common/maths/matrix/matrixUtils';
import { getZoomPointMode, ZoomPointMode } from './zoomPointModeUtils';
import { getDesiredScaleFromScroll, getMinScale } from './zoomScaleUtils';
import { calculateResetCanvasProperties } from './zoomScrollAreaSizeUtils';
import { getTransformationOrigin } from './zoomTransformationPointUtils';
import { getUpdatedTransformationMatrix } from './zoomTransformationUtils';
import { renderScrollAndTransformation, renderTransformationMatrixCSS } from './zoomTransformationRenderUtils';
import { calculateExternalScaleEventPosition } from './zoomEventPositionUtils';
import { getScrollAsPoint } from '../../utils/dom/scrollableParentUtils';
import { getMinimumCanvasDocumentSize } from '../utils/canvasSizeUtils';
import { asObject, prop } from '../../../common/utils/immutableHelper';
import animationFrameDebounce from '../../../common/utils/lib/animationFrameDebounce';
import requestSecondAnimationFrame from '../../../common/utils/lib/requestSecondAnimationFrame';

// Singletons
import zoomStateSingleton from './zoomStateSingleton';
import zoomComponentSingleton from './zoomComponentSingleton';
import platformSingleton from '../../platform/platformSingleton';

// Helper objects
import { createInertiaScrollDetector } from './inertiaScrollDetector';
import { createZoomDomElementPropertyCache } from './zoomDomElementPropertyCache';
import { hasCommandModifier, isControlKeyCommand } from '../../utils/keyboard/keyboardUtility';

// Actions
import { getCanvasZoomContentRectThunk, getZoomToFitContentRectThunk } from '../store/canvasBoundingRectActions';
import {
    resetCanvasZoomState,
    sendCanvasZoomIntercomAnalytics,
    setCanvasZoomTransformation,
} from '../store/canvasActions';
import { finishEditingCurrentElement } from '../../element/selection/selectionActions';

// Selectors
import { isShowingTemplateSelector } from '../../reducers/currentBoardId/currentBoardIdSelector';
import {
    canvasZoomScaleSelector,
    canvasZoomTargetScaleSelector,
    canvasZoomTranslationPxSelector,
} from '../store/canvasZoomSelector';

// Components
import ZoomTouchHandler from './ZoomTouchHandler';
import ZoomToFitHandler from './ZoomToFitHandler';
import CanvasScrollPositionHandler from './CanvasScrollPositionHandler';

// Constants
import { ZOOM_PROPS } from './zoomConstants';

const isRootBoardHasChanged = hasChanged('isRootBoard');
const isInboxOpenHasChanged = hasChanged('isInboxOpen');
const currentBoardIdHasChanged = hasChanged('currentBoardId');
const zoomScaleHasChanged = hasChanged('zoomScale');
const zoomTargetScaleHasChanged = hasChanged('zoomTargetScale');

// Used to track whether where currently using the wheel to zoom, scroll, or neither
const WHEEL_MODES = {
    NONE: 'NONE',
    SCROLL: 'SCROLL',
    ZOOM: 'ZOOM',
};

const mapStateToProps = createStructuredSelector({
    zoomScale: canvasZoomScaleSelector,
    zoomTranslationPx: canvasZoomTranslationPxSelector,
    zoomTargetScale: canvasZoomTargetScaleSelector,
    isShowingTemplate: isShowingTemplateSelector,
});

const mapDispatchToProps = (dispatch) => ({
    dispatchGetZoomToFitContentRectThunk: (canvasViewportNode, zoomScaleOverride, zoomTranslationOverride) =>
        dispatch(getZoomToFitContentRectThunk(canvasViewportNode, zoomScaleOverride, zoomTranslationOverride)),
    dispatchGetCanvasZoomContentRectThunk: (canvasViewportClientRect) =>
        dispatch(getCanvasZoomContentRectThunk(canvasViewportClientRect)),
    dispatchResetCanvasZoomState: () => dispatch(resetCanvasZoomState()),
    dispatchSetCanvasZoomTransformation: (args) => dispatch(setCanvasZoomTransformation(args)),
    dispatchSendCanvasZoomIntercomAnalytics: () => dispatch(sendCanvasZoomIntercomAnalytics()),
    dispatchFinishEditingCurrentElement: () => dispatch(finishEditingCurrentElement()),
});

/**
 * A component that handles the state and events for changing the Canvas's zoom level.
 */
class ZoomCanvas extends React.Component {
    constructor(props) {
        super(props);

        // Inertia scroll detector - this requires internal state
        this.inertiaScrollDetector = createInertiaScrollDetector();
        this.domElementPropertyCache = createZoomDomElementPropertyCache();

        this.ignoreNextCanvasViewportResize = true;

        // Initialise cached transformation properties
        this.resetCachedValues();
    }

    componentDidMount() {
        // Fix to ensure the canvas ref is available when the events are attached :/
        setTimeout(() => {
            const { canvasViewportRef } = this.props;
            if (!canvasViewportRef) return;

            canvasViewportRef.current.addEventListener('scroll', this.clearDomElementPropertyCache);

            // Must not be passive here as we are potentially preventing the scroll events
            canvasViewportRef.current.addEventListener('wheel', this.onWheel, { passive: false });

            // Attach a resize observer
            this.resizeObserver = new ResizeObserver(this.onCanvasViewportResize);
            this.resizeObserver.observe(canvasViewportRef.current);
        }, 0);

        // On windows machine, ctrl+wheel on non-canvas areas will result in a built-in browser zoom.
        // We want to prevent this from happening.
        window.addEventListener('wheel', (e) => e.ctrlKey && e.preventDefault(), { passive: false });

        this.setTransformationMatrix();
    }

    componentDidUpdate(prevProps) {
        if (currentBoardIdHasChanged(prevProps, this.props)) {
            this.resetCachedValues();
            this.props.dispatchResetCanvasZoomState();

            // Ignore canvas viewport size changes if it's due to the inbox changing state after changing boards
            if (isInboxOpenHasChanged(prevProps, this.props)) {
                this.ignoreNextCanvasViewportResize = true;
            }
        }

        // If we change from the root board to another board, or another board to the root board, the canvas
        // viewport size will change. In this scenario we don't want to run the canvas viewport resize logic
        if (isRootBoardHasChanged(prevProps, this.props)) {
            this.ignoreNextCanvasViewportResize = true;
        }

        // If zoom scale has been updated in the state from another place (e.g. navigation), make sure to sync the
        // zoom state to this.transformationMatrix. This is because this.transformationMatrix is not hooked to the
        // state for the purpose of performance improvement.
        if (
            zoomScaleHasChanged(prevProps, this.props) &&
            this.props.zoomScale !== matrixLib.getScale(this.transformationMatrix)
        ) {
            this.setTransformationMatrix();
        }

        // If zoom scale is updated externally, animate zoom scale
        if (zoomTargetScaleHasChanged(prevProps, this.props) && this.props.zoomTargetScale) {
            this.targetScale = this.props.zoomTargetScale;

            if (!this.rafId) this.animateScale();
        }
    }

    componentWillUnmount() {
        this.resizeObserver && this.resizeObserver.disconnect();
    }

    setTransformationMatrix() {
        const zoomScale = prop('zoomScale', this.props);
        const zoomTranslationPx = asObject(prop('zoomTranslationPx', this.props));

        let matrix = Rematrix.identity();
        matrix = matrixLib.setScale(matrix, zoomScale);
        matrix = matrixLib.setTranslation(matrix, zoomTranslationPx);

        this.transformationMatrix = matrix;
    }

    resetCachedValues = () => {
        this.currentWheelMode = 'NONE';

        // Transformation properties
        this.isCurrentlyZooming = false;
        this.isCommittingZoom = false;
        this.transformationMatrix = Rematrix.identity();
        this.transformationPoint = null;
        this.targetScale = null;
        this.scaleEventPosition = null;

        // Used to determine the zoom mode
        this.previousEventPosition = null;
        this.previousScroll = null;

        // Used to calculate accidental zooms
        this.initialZoomScale = null;
        this.overallWheelDeltaY = 0;

        this.domElementPropertyCache.clearCache();

        this.clearCachedZoomContentRect();
    };

    onCanvasViewportResize = (entries, observer) => {
        const { scrollAreaRef } = this.props;

        this.clearDomElementPropertyCache();

        const canvasViewportObserverEntry = first(entries);

        if (!canvasViewportObserverEntry) return;

        const newCanvasViewportRect = {
            width:
                canvasViewportObserverEntry.borderBoxSize?.[0]?.inlineSize ||
                canvasViewportObserverEntry.contentRect?.width,
            height:
                canvasViewportObserverEntry.borderBoxSize?.[0]?.blockSize ||
                canvasViewportObserverEntry.contentRect?.height,
        };

        // Issue #13000: Keep track of the previous canvas size so we can check whether it's changed
        //  due to Windows firing the resize observer even when the size hasn't changed
        const _previousCanvasViewportClientRect = this.previousCanvasViewportClientRect;
        this.previousCanvasViewportClientRect = newCanvasViewportRect;

        const hasViewportSizeChanged =
            !_previousCanvasViewportClientRect ||
            _previousCanvasViewportClientRect.width !== newCanvasViewportRect.width ||
            _previousCanvasViewportClientRect.height !== newCanvasViewportRect.height;

        if (!hasViewportSizeChanged) return;

        // Since resize observer is triggered on initialisation, this is to prevent the resize logic from getting
        // called on initialisation.
        // It also prevents running the resize logic when changing between the root board and another board, because
        // this will also cause a resize due to the change in height of the header
        if (this.ignoreNextCanvasViewportResize) {
            this.ignoreNextCanvasViewportResize = false;
            return;
        }

        const currentScale = matrixLib.getScale(this.transformationMatrix);

        if (currentScale >= 1) return;

        const canvasDocumentSize = getMinimumCanvasDocumentSize(this.props.canvasDocumentSize, newCanvasViewportRect);

        // We want to change the zoom and translation of the Canvas to center it into the screen
        const zoomContentRect = this.getCachedZoomContentRect(newCanvasViewportRect);

        // Determine the next scale that we should use. If the new minimum scale is beyond our current scale,
        // then grow the transformation to the new min scale. Otherwise, keep the scale constant.
        const newMinScale = getMinScale(newCanvasViewportRect, zoomContentRect);
        const nextScale = newMinScale > currentScale ? newMinScale : currentScale;

        const currentScroll = getScrollAsPoint(this.props.canvasViewportRef.current);

        const {
            canvasScroll: newCanvasScroll,
            scrollAreaSize: newScrollAreaSize,
            transformationMatrix: newTransformationMatrix,
        } = calculateResetCanvasProperties(
            null,
            this.transformationMatrix,
            newCanvasViewportRect,
            canvasDocumentSize,
            zoomContentRect,
            currentScroll,
        );

        // Then create the new transformation matrix
        this.transformationMatrix = newTransformationMatrix;
        this.transformationMatrix = matrixLib.setScale(this.transformationMatrix, nextScale);

        // Update the DOM for the View util & Zoom popup using vanilla JS
        this.updateViewUtilZoom(nextScale, this.targetScale);
        zoomComponentSingleton.getZoomPopupPanelInstance()?.manuallySetMinScale(newMinScale);

        // Then render the transform
        renderScrollAndTransformation(
            this.props.canvasViewportRef.current,
            scrollAreaRef.current,
            this.props.canvasDocumentRef.current,
            newScrollAreaSize,
            newCanvasScroll,
            this.transformationMatrix,
        );

        this.debouncedDispatchSetCanvasZoomTransformation(newScrollAreaSize);

        // Save and clear cached values
        this.previousScroll = newCanvasScroll;
        this.targetScale = null;
        this.scaleEventPosition = null;
        this.transformationPoint = null;
        this.previousEventPosition = null;
    };

    /**
     * Scrolling or resizing will invalidate the cached values.
     */
    clearDomElementPropertyCache = () => {
        this.domElementPropertyCache.clearCache();
    };

    /**
     * Only recalculates the zoom content rect (the rectangle used to centre the zoom out on)
     * if it's not already cached.
     */
    getCachedZoomContentRect = (canvasViewportClientRect) => {
        if (this.zoomContentRect) this.debouncedClearCachedZoomContentRect();

        this.zoomContentRect =
            this.zoomContentRect || this.props.dispatchGetCanvasZoomContentRectThunk(canvasViewportClientRect);
        return this.zoomContentRect;
    };

    clearCachedZoomContentRect = () => {
        this.zoomContentRect = null;
    };

    debouncedClearCachedZoomContentRect = debounce(this.clearCachedZoomContentRect, 50);

    onZoomToFitStart = () => {
        this.isZoomingToFit = true;
    };

    onZoomToFitEnd = (transformationMatrix, transformationPoint) => {
        this.isZoomingToFit = false;
        this.transformationMatrix = transformationMatrix;
        this.transformationPoint = transformationPoint;

        this.previousScroll = null;
        this.targetScale = null;
        this.scaleEventPosition = null;
        this.previousEventPosition = null;
    };

    /**
     * Handle the wheel events
     */
    onWheel = (event) => {
        // Track the wheel event in the inertiaScrollDetector's state
        this.inertiaScrollDetector.addWheelEvent(event);

        const isZoomScroll =
            hasCommandModifier(event) ||
            // The pinch and zoom on Mac trackpads gets transformed into mousewheel events with
            // the "event.ctrlKey" set to true, despite the fact it's on Mac. So to handle these
            // events we need to detect control key zooms even on Macs.
            isControlKeyCommand(event);

        if (!isZoomScroll) {
            this.scaleEventPosition = null;
            return this.onWheelScroll(event);
        }

        // If it's not an inertia scroll, then treat it as a manual scroll
        const isInertiaScroll = this.inertiaScrollDetector.isInertiaScroll();

        event.preventDefault();

        if (this.isZoomingToFit) return;

        // If there's inertia scroll, but we're not currently zooming, it must be leftover from scrolling
        // so ignore it
        if (isInertiaScroll && this.currentWheelMode !== WHEEL_MODES.ZOOM) return;

        // If there's no deltaY change then no action needs to be taken
        if (event.deltaY === 0) return;

        this.currentWheelMode = WHEEL_MODES.ZOOM;

        const { clientX, clientY, deltaY } = event;

        // If we're already animating a scroll, then we want to keep track of the target scale
        // so that we maintain smooth scrolling behaviour when performing consecutive scrolls in
        // a short period (before the animation has finished).
        const currentScale = matrixLib.getScale(this.transformationMatrix);
        const currentTargetScale = this.targetScale || currentScale;

        const { canvasViewportClientRect } = this.domElementPropertyCache.getCachedValues(
            this.props.canvasViewportRef.current,
        );

        const { canvasDocumentSize } = this.props;

        // Don't allow zooms if there's no document size as it's still loading
        if (!canvasDocumentSize.width || !canvasDocumentSize.height) return;

        const zoomContentRect = this.getCachedZoomContentRect(canvasViewportClientRect);

        // Determine what the next target scale should be, based on the current scale and the direction
        // we're scrolling, etc.
        const nextTargetScale = getDesiredScaleFromScroll(
            deltaY,
            currentTargetScale,
            currentScale,
            !isInertiaScroll,
            canvasViewportClientRect,
            canvasDocumentSize,
            zoomContentRect,
        );

        // If the target scale hasn't changed, then no need to do anything
        if (nextTargetScale === this.targetScale) return;

        this.targetScale = nextTargetScale;

        // If clientX or clientY are both 0, this is an event that is triggered externally (e.g. the zoom slider wheel
        // event). In this case, do not set the scaleEventPosition based on clientX and clientY.
        if (clientX !== 0 || clientY !== 0) {
            // Keep track of where the scale event is happening so we can zoom to and from that point if
            // we're in the correct mode.
            this.scaleEventPosition = { x: clientX, y: clientY };
        }

        // If we're not currently animating the scale, then kick off the animation as we've got a
        // new target scale
        if (!this.rafId) this.animateScale();

        this.initialZoomScale = this.initialZoomScale || currentScale;
        this.overallWheelDeltaY += deltaY;

        this.debouncedCorrectAccidentalZoom();
    };

    // In case of an accidental mini zoom (when the overall deltaY of the wheel scroll is less than 50 and only when
    // zoomed from 100%), correct the zoom back to its initial zoom scale
    correctAccidentalZoom = () => {
        const isAccidentalZoom = Math.abs(this.overallWheelDeltaY) < 50 && this.initialZoomScale === 1;

        if (isAccidentalZoom) {
            this.targetScale = this.initialZoomScale;

            if (!this.rafId) this.animateScale();
        }

        this.initialZoomScale = null;
        this.overallWheelDeltaY = 0;
    };

    debouncedCorrectAccidentalZoom = debounce(this.correctAccidentalZoom, 100);

    onWheelScroll = (event) => {
        // If it's not an inertia scroll, then treat it as a manual scroll
        const isInertiaScroll = this.inertiaScrollDetector.isInertiaScroll();

        // Left over inertia scrolling from zoom, so don't scroll!
        if (isInertiaScroll && this.currentWheelMode !== WHEEL_MODES.SCROLL) {
            this.currentWheelMode = WHEEL_MODES.NONE;
            event.preventDefault();
            return;
        }

        this.currentWheelMode = WHEEL_MODES.SCROLL;
    };

    /**
     * Recursively animates the scale while we're not at the target scale by LERPing towards the target.
     */
    animateScale = () => {
        if (this.isZoomingToFit) return;
        if (!this.props.canvasViewportRef.current) return;

        // If we have an existing animation then finish it
        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }

        if (this.scaleEventPosition === null) {
            // Zoom scale updated externally do not have a trigger event point (unlike a mouse zoom event), use
            // the canvas's scroll position to determine what point of the canvas we should zoom in/out from, and
            // caches this point until the user updates their scroll position.
            this.scaleEventPosition = calculateExternalScaleEventPosition(
                this.props.canvasViewportRef.current,
                this.props.zoomScale,
            );
        }

        const currentScale = matrixLib.getScale(this.transformationMatrix);

        // If we're already at our target then we can finish the animation
        if (currentScale === this.targetScale) return;

        if (this.targetScale < 1) {
            this.props.canvasViewportRef.current.classList.add('hide-grid');
        } else if (this.targetScale === 1) {
            this.props.canvasViewportRef.current.classList.remove('hide-grid');
        }

        // Linearly interpolate towards the target scale
        let nextScale = lerp(ZOOM_PROPS.INTERPOLATION_FACTOR, this.targetScale, currentScale);

        // If we're within 0.2% just use the target scale, so we don't end up at weird scales
        if (Math.abs(nextScale - this.targetScale) / this.targetScale < 0.002) {
            nextScale = this.targetScale;
        }

        this.performScale(nextScale);

        this.rafId = requestAnimationFrame(() => {
            this.animateScale();
            this.rafId = null;
        });
    };

    immediatelyScaleTo = (desiredScale, eventPosition) => {
        if (this.isZoomingToFit) return;

        this.targetScale = desiredScale;
        this.scaleEventPosition = eventPosition;

        if (desiredScale < 1) {
            this.props.canvasViewportRef.current.classList.add('hide-grid');
        } else if (desiredScale === 1) {
            this.props.canvasViewportRef.current.classList.remove('hide-grid');
        }

        this.performScale(desiredScale);
    };

    performScale = (desiredScale) => {
        // If we're still cleaning up the previous zoom, wait until it's finished to perform another
        // scale, otherwise it can result in bad glitches, such as the content being incorrectly
        // zoomed to
        if (this.isCommittingZoom) {
            isDebugEnabled() && console.warn('Still cleaning up previous zoom, so delaying new zoom');
            return;
        }

        let nextScale = desiredScale;

        // Update the DOM for the View util & Zoom popup using vanilla JS
        this.updateViewUtilZoom(nextScale, this.targetScale);

        // On debounce, change the canvas size and reset the position of the transformation
        this.debouncedCommitZoom();

        const currentScale = matrixLib.getScale(this.transformationMatrix);

        // If we're already at our target then we can finish the animation
        if (currentScale === nextScale) return;

        // As zoom mode sometimes change after/before 1, make sure scale of 1 is always visited in order to
        // smoothly transition between zoom modes
        if ((currentScale > 1 && nextScale < 1) || (currentScale < 1 && nextScale > 1)) {
            nextScale = 1;
        }

        this.props.canvasViewportRef.current.classList.add('zooming');
        document.body.classList.add('zooming');

        this.findPointAndUpdateTransformationMatrix(currentScale, nextScale);
        renderTransformationMatrixCSS(this.props.canvasDocumentRef.current, this.transformationMatrix);

        this.isCurrentlyZooming = true;
        zoomStateSingleton.setIsZooming(true);
        zoomStateSingleton.setZoomValue(nextScale);
    };

    /**
     * Based on the scale change, find the new transformation point and update the transformation matrix.
     */
    findPointAndUpdateTransformationMatrix = (currentScale, nextScale) => {
        const scaleDelta = nextScale - currentScale;

        const { canvasViewportClientRect, canvasScroll } = this.domElementPropertyCache.getCachedValues(
            this.props.canvasViewportRef.current,
        );
        const { canvasDocumentSize } = this.props;

        const zoomPointMode = getZoomPointMode(
            this.isCurrentlyZooming,
            this.transformationPoint,
            nextScale,
            currentScale,
            scaleDelta,
            this.scaleEventPosition,
            this.previousEventPosition,
            canvasScroll,
            this.previousScroll,
        );

        const zoomContentRect = this.getCachedZoomContentRect(canvasViewportClientRect);

        const shouldDebug =
            isDebugEnabled() &&
            zoomPointMode !== ZoomPointMode.CACHED &&
            zoomPointMode !== ZoomPointMode.CACHED_LIMITED &&
            currentScale <= 1;

        if (shouldDebug) {
            console.groupCollapsed(
                '%c -- DEBUG ZOOM: ZoomCanvas.findPointAndUpdateTransformationMatrix',
                'color: #48c2ff',
            );

            console.info('%c Inputs:', 'color: blue', {
                isCurrentlyZooming: this.isCurrentlyZooming,
                transformationPoint: this.transformationPoint,
                transformationMatrix: this.transformationMatrix,
                nextScale,
                currentScale,
                scaleDelta,
                scaleEventPosition: this.scaleEventPosition,
                previousEventPosition: this.previousEventPosition,
                previousScroll: this.previousScroll,
                canvasScroll,
                canvasDocumentSize,
                canvasViewportClientRect,
                zoomContentRect,
            });
        }

        this.transformationPoint = getTransformationOrigin(
            zoomPointMode,
            this.transformationPoint,
            this.scaleEventPosition,
            canvasViewportClientRect,
            canvasScroll,
            canvasDocumentSize,
            zoomContentRect,
            this.transformationMatrix,
        );

        this.transformationMatrix = getUpdatedTransformationMatrix(
            nextScale,
            this.transformationPoint,
            this.transformationMatrix,
            canvasScroll,
        );

        if (shouldDebug) {
            console.info('%c zoomPointMode:', 'color: green', ZoomPointMode[zoomPointMode]);
            console.info('%c this.transformationPoint:', 'color: green', this.transformationPoint);
            console.info('%c this.transformationMatrix:', 'color: green', this.transformationMatrix);
            console.groupEnd();
        }
    };

    /**
     * Commit the recent zoom to state.
     *
     * When scrolling has finished, update the size of the scrollable area to match the new
     * scale and reset the transformation and scroll to appropriate values (so that the translation
     * is back to 0,0).
     */
    commitZoom = () => {
        if (!this.isCurrentlyZooming) return;

        this.isCurrentlyZooming = false;
        this.isCommittingZoom = true;

        const {
            canvasDocumentSize,
            canvasViewportRef,
            scrollAreaRef,
            canvasDocumentRef,
            zoomScale: prevZoomScale,
            dispatchSendCanvasZoomIntercomAnalytics,
        } = this.props;

        const { canvasViewportClientRect, canvasScroll } = this.domElementPropertyCache.getCachedValues(
            canvasViewportRef.current,
        );

        const zoomContentRect = this.getCachedZoomContentRect(canvasViewportClientRect);

        const updatedCanvasProperties = calculateResetCanvasProperties(
            this.transformationPoint,
            this.transformationMatrix,
            canvasViewportClientRect,
            canvasDocumentSize,
            zoomContentRect,
            canvasScroll,
        );

        this.transformationMatrix = updatedCanvasProperties.transformationMatrix;
        this.transformationPoint = updatedCanvasProperties.transformationPoint;

        renderScrollAndTransformation(
            canvasViewportRef.current,
            scrollAreaRef.current,
            canvasDocumentRef.current,
            updatedCanvasProperties.scrollAreaSize,
            updatedCanvasProperties.canvasScroll,
            this.transformationMatrix,
        );

        this.domElementPropertyCache.clearCache();
        this.clearCachedZoomContentRect();

        const nextZoomScale = matrixLib.getScale(this.transformationMatrix);

        sendAmplitudeZoomEvent(prevZoomScale, nextZoomScale);

        // We need to ensure that the dispatched scroll area size and canvas scroll changes occur after
        // the vanilla javascript version (which occurs within a RAF in renderScrollAndTransformation)
        // so we need to delay it two animation frames. If we don't Safari has visual glitches.
        requestSecondAnimationFrame(() => {
            this.dispatchSetCanvasZoomTransformation(updatedCanvasProperties.scrollAreaSize);
            dispatchSendCanvasZoomIntercomAnalytics();
            zoomStateSingleton.setIsZooming(false);
            this.isCommittingZoom = false;
        });

        // Cache values for future comparisons
        this.previousScroll = updatedCanvasProperties.canvasScroll;
        this.previousEventPosition = this.scaleEventPosition;
    };

    debouncedCommitZoom = animationFrameDebounce(this.commitZoom);

    dispatchSetCanvasZoomTransformation = (scrollAreaSize, mode) => {
        const { dispatchSetCanvasZoomTransformation } = this.props;

        dispatchSetCanvasZoomTransformation({
            scale: matrixLib.getScale(this.transformationMatrix),
            translation: matrixLib.getTranslation(this.transformationMatrix),
            scrollAreaSize,
            mode,
        });
    };

    debouncedDispatchSetCanvasZoomTransformation = debounce(this.dispatchSetCanvasZoomTransformation, 100);

    updateViewUtilZoom = (nextScale, targetScale) => {
        // Update the DOM for the View util & Zoom popup using vanilla JS
        zoomComponentSingleton.getViewUtilInstance()?.manuallySetZoomValue(nextScale * 100);
        zoomComponentSingleton.getZoomPopupPanelInstance()?.manuallySetZoomValue(nextScale, targetScale);
    };

    /**
     * When the canvas origin is moved, we might need to add some translation to the transformation matrix
     * to prevent the view from jumping.
     * E.g. If the scroll is at the top of the viewport already and then we return the canvas origin to
     * its previous location, we need to compensate for the change in origin by adding translation.
     */
    setCanvasDocumentTranslation = (translation) => {
        const { canvasDocumentRef } = this.props;

        this.transformationMatrix = matrixLib.setTranslation(this.transformationMatrix, translation);

        renderTransformationMatrixCSS(canvasDocumentRef.current, this.transformationMatrix);
        this.dispatchSetCanvasZoomTransformation();
    };

    getZoomCanvasTransformationMatrix = () => this.transformationMatrix;
    getZoomCanvasTransformationPoint = () => this.transformationPoint;

    render() {
        const { canvasViewportRef, canvasDocumentSize, dispatchFinishEditingCurrentElement } = this.props;
        return (
            <>
                {platformSingleton.features.isTouch && (
                    <ZoomTouchHandler
                        canvasViewportRef={canvasViewportRef}
                        canvasDocumentSize={canvasDocumentSize}
                        immediatelyScaleTo={this.immediatelyScaleTo}
                        domElementPropertyCache={this.domElementPropertyCache}
                        getZoomCanvasTransformationMatrix={this.getZoomCanvasTransformationMatrix}
                        dispatchFinishEditingCurrentElement={dispatchFinishEditingCurrentElement}
                    />
                )}
                <ZoomToFitHandler
                    {...this.props}
                    onZoomToFitStart={this.onZoomToFitStart}
                    onZoomToFitEnd={this.onZoomToFitEnd}
                    domElementPropertyCache={this.domElementPropertyCache}
                    dispatchSetCanvasZoomTransformation={this.dispatchSetCanvasZoomTransformation}
                    updateViewUtilZoom={this.updateViewUtilZoom}
                    getZoomCanvasTransformationMatrix={this.getZoomCanvasTransformationMatrix}
                    getZoomCanvasTransformationPoint={this.getZoomCanvasTransformationPoint}
                />
                <CanvasScrollPositionHandler
                    {...this.props}
                    getZoomCanvasTransformationMatrix={this.getZoomCanvasTransformationMatrix}
                    setCanvasDocumentTranslation={this.setCanvasDocumentTranslation}
                />
            </>
        );
    }
}

ZoomCanvas.propTypes = {
    isRootBoard: PropTypes.bool,
    isInboxOpen: PropTypes.bool,

    zoomMode: PropTypes.string,
    zoomScale: PropTypes.number,
    zoomTranslationPx: PropTypes.object,
    zoomTargetScale: PropTypes.number,

    canvasViewportRef: PropTypes.object,
    canvasDocumentRef: PropTypes.object,
    scrollAreaRef: PropTypes.object,
    canvasDocumentSize: PropTypes.object,

    gridSize: PropTypes.number,
    canvasOrigin: PropTypes.object,

    currentBoardId: PropTypes.string,

    dispatchResetCanvasZoomState: PropTypes.func,
    dispatchSetCanvasZoomTransformation: PropTypes.func,
    dispatchGetCanvasZoomContentRectThunk: PropTypes.func,
    dispatchGetZoomToFitContentRectThunk: PropTypes.func,

    dispatchSendCanvasZoomIntercomAnalytics: PropTypes.func,
    dispatchFinishEditingCurrentElement: PropTypes.func,
};

export default connect(mapStateToProps, mapDispatchToProps)(ZoomCanvas);
